/**
 * Ad Attribution & Marketing Tracking Utility
 * 
 * Accurately detects and tracks incoming marketing traffic:
 * - Facebook Ads (utm_source=facebook/instagram, fbclid, meta referrers)
 * - TikTok Ads (utm_source=tiktok, ttclid, tiktok referrers)
 * - Google Ads (utm_source=google, gclid, google referrers)
 * - Organic / Direct traffic
 * 
 * Preserves attribution across multi-page browsing in localStorage (30-day window)
 * so checkout inherits the original ad attribution even after query parameters disappear.
 */

export interface AdAttributionData {
  traffic_source: 'facebook' | 'tiktok' | 'google' | 'organic' | 'manual';
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  click_id?: string;
  landing_page?: string;
  referrer_url?: string;
  timestamp?: number;
}

const STORAGE_KEY = 'online_store_ad_attribution';
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Determine canonical traffic source from URL params, click IDs, and referrer
 */
export function determineTrafficSource(
  utmSource: string | null,
  fbclid: string | null,
  ttclid: string | null,
  gclid: string | null,
  referrer: string
): { source: 'facebook' | 'tiktok' | 'google' | 'organic'; clickId?: string } {
  const normSource = (utmSource || '').toLowerCase().trim();
  const normRef = (referrer || '').toLowerCase();

  // 1. TikTok Ads check (Explicit utm or ttclid or referrer)
  if (
    ttclid ||
    normSource.includes('tiktok') ||
    normSource === 'tt' ||
    normRef.includes('tiktok.com') ||
    normRef.includes('musical.ly')
  ) {
    return { source: 'tiktok', clickId: ttclid || undefined };
  }

  // 2. Facebook / Instagram Ads check (Explicit utm or fbclid or referrer)
  if (
    fbclid ||
    normSource.includes('facebook') ||
    normSource.includes('instagram') ||
    normSource === 'fb' ||
    normSource === 'ig' ||
    normSource === 'meta' ||
    normRef.includes('facebook.com') ||
    normRef.includes('instagram.com') ||
    normRef.includes('messenger.com')
  ) {
    return { source: 'facebook', clickId: fbclid || undefined };
  }

  // 3. Google Ads check
  if (
    gclid ||
    normSource.includes('google') ||
    normSource === 'adwords'
  ) {
    return { source: 'google', clickId: gclid || undefined };
  }

  return { source: 'organic' };
}

/**
 * Initialize ad tracking on application load or navigation.
 * Extracts UTM tags, click IDs, and referrers.
 */
export function initAdTracker(): AdAttributionData {
  if (typeof window === 'undefined') {
    return { traffic_source: 'organic' };
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmContent = urlParams.get('utm_content');
    const utmTerm = urlParams.get('utm_term');

    const fbclid = urlParams.get('fbclid');
    const ttclid = urlParams.get('ttclid');
    const gclid = urlParams.get('gclid');
    const referrer = document.referrer || '';
    const currentUrl = window.location.href;

    const hasNewAdParams = Boolean(
      utmSource || utmCampaign || fbclid || ttclid || gclid
    );

    const { source, clickId } = determineTrafficSource(
      utmSource,
      fbclid,
      ttclid,
      gclid,
      referrer
    );

    // If new ad parameters are detected, record a new attribution session
    if (hasNewAdParams || (source !== 'organic' && referrer)) {
      const attribution: AdAttributionData = {
        traffic_source: source,
        utm_source: utmSource || (source === 'facebook' ? 'facebook' : source === 'tiktok' ? 'tiktok' : undefined),
        utm_medium: utmMedium || (fbclid || ttclid || gclid ? 'paid_ad' : undefined),
        utm_campaign: utmCampaign || undefined,
        utm_content: utmContent || undefined,
        utm_term: utmTerm || undefined,
        click_id: clickId,
        landing_page: currentUrl,
        referrer_url: referrer || undefined,
        timestamp: Date.now(),
      };

      // Save to localStorage & sessionStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
      } catch (e) {
        console.warn('Unable to persist ad attribution:', e);
      }

      return attribution;
    }

    // Otherwise, check existing attribution in storage (within 30-day window)
    const existing = getStoredAttribution();
    if (existing) {
      return existing;
    }

    // Direct / Organic fallback
    const defaultAttribution: AdAttributionData = {
      traffic_source: 'organic',
      landing_page: currentUrl,
      referrer_url: referrer || undefined,
      timestamp: Date.now(),
    };

    try {
      if (!sessionStorage.getItem(STORAGE_KEY)) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(defaultAttribution));
      }
    } catch {}

    return defaultAttribution;
  } catch (err) {
    console.error('Error in initAdTracker:', err);
    return { traffic_source: 'organic' };
  }
}

/**
 * Retrieve active ad attribution data from storage
 */
export function getStoredAttribution(): AdAttributionData | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data: AdAttributionData = JSON.parse(raw);
    if (data.timestamp && Date.now() - data.timestamp < ATTRIBUTION_WINDOW_MS) {
      return data;
    }
  } catch (e) {
    console.warn('Error reading stored ad attribution:', e);
  }

  return null;
}

/**
 * Returns attribution payload ready to attach to order/preorder submission.
 */
export function getAdAttributionData(): Partial<AdAttributionData> {
  const current = getStoredAttribution() || initAdTracker();

  return {
    traffic_source: current.traffic_source || 'organic',
    utm_source: current.utm_source || undefined,
    utm_medium: current.utm_medium || undefined,
    utm_campaign: current.utm_campaign || undefined,
    utm_content: current.utm_content || undefined,
    utm_term: current.utm_term || undefined,
    click_id: current.click_id || undefined,
    landing_page: current.landing_page || undefined,
    referrer_url: current.referrer_url || undefined,
  };
}
